/* eslint-disable react/prop-types, no-underscore-dangle */
import React from 'react';

import { Header, Grid, Breadcrumb, Button, Label, Card } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _head from 'lodash/head';
import _isEmpty from 'lodash/isEmpty';

import {
  Image,
  InternalLink,
  MarkdownContent,
  SEO,
  Price,
  OutboundLink,
  CatalogItemShareButton,
  HorizontalImageCard,
  FishermanIcon,
  CatalogItemLikeButton,
  ServiceItemSchema,
  BreadcrumbListSchema,
  WebsiteComponentInteraction,
  BasicCarousel,
  componentIterator,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  ModifierSetsAccordions,
  FadeReveal,
  Section,
  PriceRange,
} from '@bluefin/components';

import ItemLocationAvailability from '../../components/ItemLocationAvailability/ItemLocationAvailability';
import ServiceItemAttributeList from '../../components/ServiceItemAttributeList';
import ServiceItemDuration from '../../components/ServiceItemDuration';
import TeamMemberLinkList from '../../components/TeamMemberLinkList';
import { createServiceCategorySlug, createServiceItemSlug } from '../../index';
import {
  getBookNowPage,
  getBusinessFiles,
  getFirstBusinessFile,
  getCtaInteractions,
  getServiceItemModifierSets,
  getOtherCategoryItems,
  getComponentContentNodeContent,
  getServiceTeamMembers,
  createVariationShortDescription,
} from '../../template-utils';
import { constructFullPageUrl, ensureSlugSlashes, getPageUrl } from '../../utils';

export default function ServiceItemTemplate({ data, pageContext }) {
  const {
    fishermanBusinessMenuItem,
    fishermanBusiness: business = {},
    allFishermanBusinessMenuCategory: { nodes: categories },
    allFishermanBusinessMenuItem: { nodes: allItems },
    allFishermanBusinessMenuModifierSet: { nodes: allModifierSets },
    allFishermanBusinessMenuModifier: { nodes: allModifiers },
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    fishermanBusinessWebsitePage: { components: contentNodeComponents = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] },
    allFishermanBusinessTeam: { nodes: allTeamMembers = [] } = {},
    allFishermanBusinessLocation: { nodes: allLocations = [] } = {},
    site: {
      siteMetadata: { baseUrl },
    },
  } = data;
  const {
    fishermanParentPageTitle = 'Services',
    fishermanParentPageSlug = '/services/',
    templateProps = {},
  } = pageContext;
  const {
    relatedServiceItemsText = 'Other Services',
    optionsText = 'Options',
    additionsText = 'Additions',
    priceRangeProps = {},
  } = templateProps;

  const {
    name: businessName,
    _id: businessId,
    type: businessType,
    businessCategories = [],
    website,
  } = business;

  const {
    _id,
    name,
    description,
    fullDescription,
    variations,
    files,
    available,
    interactions = [],
    annotations: { likeCount = 0 } = {},
    seoTitle,
    seoDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
    duration,
    durationUnit,
    extraData: { attributes: itemAttributes = [] } = {},
  } = fishermanBusinessMenuItem;

  const url = getPageUrl(website, [
    createServiceItemSlug(fishermanBusinessMenuItem, allItems, categories),
  ]);

  function getItemCategory() {
    return categories.find(({ items }) => {
      return items.includes(_id);
    });
  }

  function getItemFiles() {
    return getBusinessFiles(files, allFiles);
  }

  const itemCategory = getItemCategory();
  const bookingPage = getBookNowPage(allPages);
  const otherCategoryItems = getOtherCategoryItems(_id, itemCategory, allItems);
  const itemFiles = getItemFiles();
  const itemModifierSets = getServiceItemModifierSets(fishermanBusinessMenuItem, allModifierSets);
  const filteredInteractions = getCtaInteractions(interactions);
  const { name: categoryName } = itemCategory || {};
  const hasSingleVariation = variations.length === 1;
  const teamMembers = getServiceTeamMembers(fishermanBusinessMenuItem, allTeamMembers);

  const serviceOptionsHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_options_header',
    defaultValue: optionsText,
  });
  const serviceAdditionsHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'service_additions_header',
    defaultValue: additionsText,
  });
  const otherServiceItemsHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'related_service_items_header',
  });

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={name}
        title={seoTitle}
        description={seoDescription || description}
        truncateDescription
        images={itemFiles}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        ogUrl={url}
      />
      <ServiceItemSchema
        businessName={businessName}
        businessCategories={businessCategories}
        name={name}
        description={description}
        categoryName={categoryName}
        duration={duration}
        durationUnit={durationUnit}
        prices={variations.map((variation) => variation.price)}
        image={_head(itemFiles)}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, fishermanParentPageSlug),
          },
          {
            name: `Services - ${categoryName} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, createServiceCategorySlug(itemCategory, categories)),
          },
          { name: `${name} | ${businessName}` },
        ]}
      />
      <div className="service-item-page-container">
        <Breadcrumb>
          <Breadcrumb.Section
            link
            to={ensureSlugSlashes(fishermanParentPageSlug)}
            as={InternalLink}
          >
            <FishermanIcon iconName="fancy-arrow-left" />
            {fishermanParentPageTitle}
          </Breadcrumb.Section>
          <Breadcrumb.Divider content="/" />
          <Breadcrumb.Section
            link
            to={createServiceCategorySlug(itemCategory, categories)}
            as={InternalLink}
          >
            {categoryName}
          </Breadcrumb.Section>
          <Breadcrumb.Divider content="/" />
          <Breadcrumb.Section>{name}</Breadcrumb.Section>
        </Breadcrumb>
        <Section className="item-details-section">
          <Grid className="item-details-header" stackable>
            <Grid.Column width={7} className="information-column">
              <div>
                <Header as="h1">{name}</Header>
                <div className="item-actions">
                  <CatalogItemLikeButton
                    businessId={businessId}
                    businessType={businessType}
                    catalogItemId={_id}
                    initialLikeCount={likeCount}
                    event={{
                      category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.SERVICES_LIKE_ITEM_EVENT_ACTION,
                      label: name,
                    }}
                  />
                  <CatalogItemShareButton
                    url={constructFullPageUrl(
                      baseUrl,
                      createServiceItemSlug(fishermanBusinessMenuItem, allItems, categories),
                    )}
                    event={{
                      category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.SERVICES_SHARE_ITEM_EVENT_ACTION,
                      label: name,
                    }}
                  />
                </div>
                {hasSingleVariation && (
                  <Price amount={variations[0].price} displayZeroCost={false} />
                )}
                {!available && <Label className="unavailable">Unavailable</Label>}
                <MarkdownContent content={description} className="item-description" />
                <ServiceItemDuration
                  className="attribute-information"
                  duration={duration}
                  durationUnit={durationUnit}
                />
                {!_isEmpty(teamMembers) && (
                  <TeamMemberLinkList
                    className="attribute-information"
                    teamMembers={teamMembers}
                    allTeamMembers={allTeamMembers}
                  />
                )}
                <ItemLocationAvailability
                  className="attribute-information"
                  item={fishermanBusinessMenuItem}
                  locations={allLocations}
                />
                <div className="ctas-container">
                  {bookingPage && _isEmpty(filteredInteractions) && (
                    <Button
                      secondary
                      as={OutboundLink}
                      to={bookingPage.url}
                      event={{
                        category: EVENT_CATEGORIES.BOOKING_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.BOOKING_SCHEDULE_APPOINTMENT_INTENT_EVENT_ACTION,
                      }}
                    >
                      {bookingPage.title}
                    </Button>
                  )}
                  {filteredInteractions.map((interaction) => {
                    return (
                      <WebsiteComponentInteraction
                        key={interaction._id}
                        interaction={interaction}
                        className="service-category-interaction"
                        displayComponentPropsMap={{
                          button: {
                            size: null,
                            compact: false,
                          },
                        }}
                        event={{
                          category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                          action: EVENT_ACTIONS.SERVICES_ITEM_INTERACTION_ACTION,
                        }}
                      />
                    );
                  })}
                </div>
                {fullDescription && (
                  <MarkdownContent content={fullDescription} className="item-full-description" />
                )}
                <ServiceItemAttributeList attributes={itemAttributes} />
              </div>
            </Grid.Column>
            {itemFiles.length > 0 && (
              <Grid.Column width={9} textAlign="center" className="image-column">
                <FadeReveal triggerOnce>
                  <BasicCarousel
                    className="service-object-images"
                    size={1}
                    grouping={1}
                    displayIndicators={false}
                    displayPlayPauseButton={false}
                    autoScroll={false}
                    leftArrowIcon="fancy-arrow-left"
                    rightArrowIcon="fancy-arrow-right"
                    slide
                    items={componentIterator({
                      iterator: itemFiles,
                      component: (
                        <Image background className="item-image-item" loadStrategy="eager" />
                      ),
                      propMap: { src: 'src', altText: 'altText' },
                    })}
                  />
                </FadeReveal>
              </Grid.Column>
            )}
          </Grid>
          <Grid className="item-price-details" stackable>
            {!hasSingleVariation && (
              <Grid.Column width={8} className="variations-column">
                <Header>{serviceOptionsHeaderContent}</Header>
                <div className="service-item-variations-container">
                  {variations.map((variation) => {
                    const {
                      _id: variationId,
                      name: variationName,
                      price: variationPrice,
                    } = variation;

                    const extraData = createVariationShortDescription(variation);

                    return (
                      <div key={variationId} className="service-item-variation-container">
                        <div>
                          <div className="header-container">
                            <Header>{variationName}</Header>
                            <Price amount={variationPrice} displayZeroCost={false} />
                          </div>
                          {!_isEmpty(extraData) && (
                            <div className="extra-data-container">
                              <MarkdownContent content={extraData} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Grid.Column>
            )}
            {!_isEmpty(itemModifierSets) && (
              <Grid.Column width={8} className="modifiers-column">
                <Header>{serviceAdditionsHeaderContent}</Header>
                <ModifierSetsAccordions
                  modifierSets={itemModifierSets}
                  modifiers={allModifiers}
                  displayDescription
                  displayMinMaxAllowed
                />
              </Grid.Column>
            )}
          </Grid>
        </Section>

        {otherCategoryItems.length > 0 && (
          <Section textAlign="center" className="related-services-section">
            <Header as="h2">{otherServiceItemsHeaderContent || relatedServiceItemsText}</Header>
            <Card.Group itemsPerRow={2} className="related-items-grid" stackable>
              {otherCategoryItems.map((item) => {
                const {
                  _id: itemId,
                  name: itemName,
                  description: itemDescription,
                  variations: itemVariations = [],
                  files: otherItemImages,
                } = item;

                const otherItemFile = getFirstBusinessFile(otherItemImages, allFiles);
                const { src: otherItemFileSrc, altText: otherItemFileAlt } = otherItemFile || {};

                return (
                  <FadeReveal triggerOnce className="ui card" key={itemId}>
                    <HorizontalImageCard
                      imagePosition="right"
                      image={otherItemFileSrc}
                      imageAsBackground
                      link
                      altText={otherItemFileAlt}
                      as={InternalLink}
                      to={createServiceItemSlug(item, allItems, categories)}
                      event={{
                        category: EVENT_CATEGORIES.SERVICES_EVENT_CATEGORY,
                        action: EVENT_ACTIONS.SERVICES_VIEW_ITEM_DETAILS_EVENT_ACTION,
                        label: itemName,
                      }}
                    >
                      <Card.Header className="header-container">
                        <Card.Header>{itemName}</Card.Header>
                        <PriceRange
                          prices={itemVariations}
                          priceKey="price"
                          settings={{
                            displayType: 'range',
                            displayZeroCost: false,
                          }}
                          {...(priceRangeProps || {})}
                        />
                      </Card.Header>
                      <MarkdownContent content={itemDescription} />
                      <FishermanIcon iconName="arrow right" />
                    </HorizontalImageCard>
                  </FadeReveal>
                );
              })}
            </Card.Group>
          </Section>
        )}
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: String!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    fishermanBusiness {
      _id
      type
      name
      businessCategories {
        category
      }
      website {
        domains {
          url
          primaryDomain
        }
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
      }
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Services" }) {
      components {
        componentIdentifier
        contentNodes {
          content
        }
      }
    }
    allFishermanBusinessMenuCategory {
      nodes {
        _id
        items
        name
        files
      }
    }
    allFishermanBusinessMenuItem(sort: { order: ASC, fields: order }) {
      nodes {
        description
        _id
        name
        variations {
          _id
          menuItem
          name
          order
          price
        }
        annotations {
          likeCount
        }
        visible
        available
        files
        modifierSets
      }
    }
    allFishermanBusinessMenuModifierSet(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        order
        modifiers
        minAllowed
        maxAllowed
        description
      }
    }
    allFishermanBusinessMenuModifier(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        description
        order
        price
      }
    }
    fishermanBusinessMenuItem(_id: { eq: $id }) {
      _id
      name
      description
      fullDescription
      files
      interactions {
        _id
        displayType
        behaviorType
        url
        enabled
        style
        label
      }
      variations {
        _id
        menuItem
        name
        order
        price
        shortDescription
        duration
        durationUnit
      }
      annotations {
        likeCount
      }
      visible
      available
      modifierSets
      seoTitle
      seoDescription
      seoOgTitle
      seoOgDescription
      seoOgImage {
        url
        alt
        type
      }
      duration
      durationUnit
      extraData {
        attributes {
          description
          icon
          iconLibrary
          label
          type
        }
      }
      locations {
        location
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessTeam(sort: { order: ASC, fields: order }, filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        show
        associatedItemVariations {
          itemId
          variationId
          item {
            _id
            name
            variations {
              _id
              name
            }
            visible
          }
        }
      }
    }
    allFishermanBusinessLocation {
      nodes {
        _id
        name
        city
        street
      }
    }
  }
`;
